import React from 'react'
import { format } from 'date-fns'
import { ArrowRight, Mail, ExternalLink, Bell, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn, formatPhoneDisplay } from '@/lib/utils'
import type { CalendarBooking } from './BookingsCalendar'
import { getBookingCalendarDate } from './BookingsCalendar'

export interface RescheduleConfirmation {
  start: Date
  end: Date
  sendClientEmail: boolean
  updateConnectedCalendar: boolean
  resendAttorneyReminder: boolean
}

interface RescheduleConfirmDialogProps {
  open: boolean
  booking: CalendarBooking | null
  /** Tentative new start derived from the drag drop target. User can fine-tune
      the date and time inside the dialog before confirming. */
  proposedStart: Date | null
  onCancel: () => void
  onConfirm: (result: RescheduleConfirmation) => void | Promise<void>
}

const DEFAULT_DURATION_MIN = 30

export default function RescheduleConfirmDialog({
  open,
  booking,
  proposedStart,
  onCancel,
  onConfirm,
}: RescheduleConfirmDialogProps) {
  const [sendClientEmail, setSendClientEmail] = React.useState(true)
  const [updateConnectedCalendar, setUpdateConnectedCalendar] = React.useState(true)
  const [resendAttorneyReminder, setResendAttorneyReminder] = React.useState(true)
  const [submitting, setSubmitting] = React.useState(false)

  // Reset side-effect toggles whenever a new drop opens the dialog. The new
  // start time itself comes straight from `proposedStart` — we don't ask the
  // user to re-enter it, the drag was the choice.
  React.useEffect(() => {
    if (open && proposedStart) {
      setSendClientEmail(true)
      setUpdateConnectedCalendar(true)
      setResendAttorneyReminder(true)
      setSubmitting(false)
    }
  }, [open, proposedStart])

  if (!booking) return null

  const original = getBookingCalendarDate(booking)
  const originalLabel = format(original, 'EEE, MMM d · h:mm a')
  const newStart = proposedStart
  const newEnd = newStart ? new Date(newStart.getTime() + DEFAULT_DURATION_MIN * 60 * 1000) : null
  const isPast = newStart ? newStart.getTime() < Date.now() - 60_000 : false
  const isUnchanged = newStart ? Math.abs(newStart.getTime() - original.getTime()) < 60_000 : false
  const canSubmit = !!newStart && !isPast && !isUnchanged && !submitting

  const handleConfirm = async () => {
    if (!newStart || !newEnd) return
    setSubmitting(true)
    try {
      await onConfirm({
        start: newStart,
        end: newEnd,
        sendClientEmail,
        updateConnectedCalendar,
        resendAttorneyReminder,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const contactLine =
    booking.callerName ||
    (booking.phone ? formatPhoneDisplay(booking.phone) : null) ||
    'this booking'

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onCancel() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule {contactLine}?</DialogTitle>
          <DialogDescription>
            Confirm the new time and what to send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Read-only summary of the move the user just performed via drag.
              We deliberately don't render <input type="date|time"> here —
              tapping those on iOS pops a giant native picker, undoing the
              point of the drag. If the user wants a different time they
              can Cancel and drag again. */}
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2.5">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground line-through">{originalLabel}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className={cn(
                'font-semibold',
                isPast ? 'text-rose-600 dark:text-rose-400' : 'text-brand-royal'
              )}>
                {newStart ? format(newStart, 'EEE, MMM d · h:mm a') : '—'}
              </span>
            </div>
          </div>

          {isPast && (
            <p className="text-xs text-rose-600 dark:text-rose-400">
              That's in the past — cancel and drop on a future slot instead.
            </p>
          )}
          {isUnchanged && (
            <p className="text-xs text-muted-foreground">
              Same as the current time — nothing to change.
            </p>
          )}

          <div className="border-t border-border/50 pt-3 space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              When this saves
            </p>

            <CheckboxRow
              icon={Mail}
              label="Email client about new time"
              hint="Sends an updated calendar invite that replaces the old one."
              checked={sendClientEmail}
              onChange={setSendClientEmail}
            />
            <CheckboxRow
              icon={ExternalLink}
              label="Update connected calendar"
              hint="Patches the Nylas event in place if one is connected. No-op otherwise."
              checked={updateConnectedCalendar}
              onChange={setUpdateConnectedCalendar}
            />
            <CheckboxRow
              icon={Bell}
              label="Resend attorney reminder"
              hint="Re-queues the heads-up alert before the new appointment time."
              checked={resendAttorneyReminder}
              onChange={setResendAttorneyReminder}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              'Reschedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CheckboxRow({ icon: Icon, label, hint, checked, onChange }: {
  icon: React.ElementType
  label: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className={cn(
      'flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent/50 transition-colors',
      !checked && 'opacity-70'
    )}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-border text-brand-royal focus:ring-brand-royal"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <Icon className="h-3 w-3 text-muted-foreground" />
          {label}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
      </div>
    </label>
  )
}
